import { useAuthStore } from '@/stores/auth'

const API_BASE = import.meta.env.VITE_API_URL || '/api'

interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: string
  message?: string
}

class ApiClient {
  private baseUrl: string

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const { token } = useAuthStore.getState()

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token ? { 'X-Session-Token': token } : {}),
      ...options.headers,
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers,
      credentials: 'include',
    })

    const data: ApiResponse<T> = await response.json()

    if (!data.success) {
      // Handle auth errors
      if (response.status === 401) {
        useAuthStore.getState().logout()
      }
      throw new Error(data.error || 'Request failed')
    }

    return data.data as T
  }

  // Auth
  async register(email: string, password: string, name?: string) {
    return this.request<{ user: User; token: string }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, name }),
    })
  }

  async login(email: string, password: string) {
    return this.request<{ user: User; token: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
  }

  async logout() {
    return this.request<void>('/auth/logout', { method: 'POST' })
  }

  async getSession() {
    return this.request<{ user: User }>('/auth/session')
  }

  async getCloudflareAuthUrl() {
    return this.request<{ authUrl: string }>('/auth/cloudflare/connect')
  }

  async disconnectCloudflare() {
    return this.request<void>('/auth/cloudflare/disconnect', { method: 'POST' })
  }

  async getCloudflareStatus() {
    return this.request<{
      connected: boolean
      zones: number
      pagesProjects: number
      workers: number
    }>('/auth/cloudflare/status')
  }

  // Profile
  async updateProfile(data: { name?: string; email?: string }) {
    return this.request<{ user: User }>('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async changePassword(data: { currentPassword: string; newPassword: string }) {
    return this.request<void>('/auth/password', {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteAccount() {
    return this.request<void>('/auth/account', { method: 'DELETE' })
  }

  // Domains
  async getDomains() {
    return this.request<{ domains: Domain[] }>('/domains')
  }

  async addDomain(domainName: string) {
    return this.request<{ domain: Domain }>('/domains', {
      method: 'POST',
      body: JSON.stringify({ domainName }),
    })
  }

  async getDomain(id: string) {
    return this.request<{ domain: Domain }>(`/domains/${id}`)
  }

  async updateDomain(id: string, data: Partial<Domain>) {
    const payload: Record<string, unknown> = { ...data }
    if ('monetization_strategy' in data) {
      payload.monetizationStrategy = data.monetization_strategy
      delete payload.monetization_strategy
    }
    return this.request<void>(`/domains/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    })
  }

  async deleteDomain(id: string) {
    return this.request<void>(`/domains/${id}`, { method: 'DELETE' })
  }

  async verifyDomain(id: string) {
    return this.request<{ verified: boolean; zoneId?: string }>(`/domains/${id}/verify`, {
      method: 'POST',
    })
  }

  async analyzeDomain(id: string) {
    return this.request<{ analysis: DomainAnalysis }>(`/domains/${id}/analyze`, {
      method: 'POST',
    })
  }

  async getDomainOperations(id: string) {
    return this.request<{
      connected: boolean
      verificationStatus: string
      zone: null | {
        id: string
        status: string
        plan: string
        nameservers: string[]
      }
      dns: null | {
        total: number
        byType: Record<string, number>
      }
      pages: null | {
        projectName: string | null
        hasProject: boolean
        projectCount: number
        subdomain?: string
        deployedUrl?: string | null
      }
      recommendations: string[]
      cloudflareError?: string
    }>(`/domains/${id}/operations`)
  }

  async getDnsStatus(id: string) {
    return this.request<{
      configured: boolean
      zoneId?: string
      pagesProject?: string | null
      deployedUrl?: string | null
      expectedTarget?: string | null
      records?: {
        root: Array<{ type: string; content: string; proxied: boolean }>
        www: Array<{ type: string; content: string; proxied: boolean }>
      }
      rootPointsToPages?: boolean
      wwwPointsToPages?: boolean
      issues?: string[]
      recommendations?: string[]
      message?: string
      error?: string
    }>(`/domains/${id}/dns-status`)
  }

  // Sites
  async getSites() {
    return this.request<{ sites: Site[] }>('/sites')
  }

  async getSite(id: string) {
    return this.request<{ site: Site }>(`/sites/${id}`)
  }

  async analyzeSite(domainId: string, concept?: string) {
    return this.request<{
      analysis: DomainAnalysis
      questions: ClarifyingQuestion[]
      recommendedStrategy: string
      strategies: Array<{ strategy: string; fitScore: number; reasoning: string }>
    }>('/sites/analyze', {
      method: 'POST',
      body: JSON.stringify({ domainId, concept }),
    })
  }

  async generateSite(domainId: string, strategy?: string, customAnswers?: Record<string, string>) {
    return this.request<{ site: Site }>('/sites/generate', {
      method: 'POST',
      body: JSON.stringify({ domainId, strategy, customAnswers }),
    })
  }

  async updateSite(id: string, data: Partial<Site>) {
    return this.request<void>(`/sites/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteSite(id: string) {
    return this.request<void>(`/sites/${id}`, { method: 'DELETE' })
  }

  async getSitePreview(id: string) {
    const response = await fetch(`${this.baseUrl}/sites/${id}/preview`, {
      credentials: 'include',
      headers: {
        'X-Session-Token': useAuthStore.getState().token || '',
      },
    })
    return response.text()
  }

  async deploySite(id: string) {
    return this.request<{ deploymentId: string; url: string; customDomain?: string }>(
      `/sites/${id}/deploy`,
      { method: 'POST' }
    )
  }

  async regenerateSite(id: string, regenerate: 'content' | 'design' | 'all') {
    return this.request<{ content?: SiteContent; design?: DesignConfig }>(
      `/sites/${id}/regenerate`,
      {
        method: 'POST',
        body: JSON.stringify({ regenerate }),
      }
    )
  }

  // Analytics
  async getAnalyticsOverview(timeRange: string = '30d') {
    const data = await this.request<any>(`/analytics/overview?timeRange=${timeRange}`)

    const overview = data.overview || {
      totalPageviews: data.totalVisits || 0,
      pageviewsChange: 0,
      uniqueVisitors: 0,
      visitorsChange: 0,
      totalLeads: data.totalLeads || 0,
      leadsChange: 0,
      conversionRate: data.conversionRate || 0,
      conversionChange: 0,
    }

    const dailyStats = data.dailyStats || []

    const topSites = (data.topSites || []).map((site: any) => ({
      id: site.id,
      domainName: site.domainName || site.domain_name,
      pageviews: site.pageviews ?? site.total_visits ?? 0,
      leads: site.leads ?? site.total_leads ?? 0,
    }))

    const recentEvents = (data.recentEvents || data.recentActivity || []).map((event: any) => ({
      type: event.type || event.event_type,
      domainName: event.domainName || event.domain_name,
      siteId: event.siteId || event.site_id,
      timestamp: event.timestamp || (event.created_at ? event.created_at * 1000 : Date.now()),
    }))

    return { overview, dailyStats, topSites, recentEvents }
  }

  async getSiteAnalytics(siteId: string) {
    return this.request<{
      site: { id: string; domainName: string; totalVisits: number; totalLeads: number }
      events: Array<{ event_type: string; count: number }>
      dailyVisits: Array<{ date: string; visits: number }>
      topReferrers: Array<{ referrer: string; count: number }>
      uniqueVisitors: number
    }>(`/analytics/sites/${siteId}`)
  }

  // Leads
  async getLeads(siteId?: string) {
    const query = siteId ? `?siteId=${siteId}` : ''
    const data = await this.request<any>(`/leads${query}`)
    return {
      leads: (data.leads || []).map(mapLead),
      pagination: data.pagination,
    }
  }

  async getLead(id: string) {
    const data = await this.request<any>(`/leads/${id}`)
    return { lead: mapLead(data.lead) }
  }

  async updateLead(
    id: string,
    data: { status?: string; notes?: string; assignedTo?: string | null }
  ) {
    return this.request<void>(`/leads/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteLead(id: string) {
    return this.request<void>(`/leads/${id}`, { method: 'DELETE' })
  }

  async getLeadStats() {
    return this.request<{
      total: number
      byStatus: Record<string, number>
      byDomain: Array<{ domain_name: string; lead_count: number }>
      recent: Array<{ email: string; name: string; created_at: number; domain_name: string }>
    }>('/leads/stats')
  }

  async exportLeads(siteId?: string, format: 'csv' | 'json' = 'csv') {
    const query = new URLSearchParams()
    if (siteId) query.set('siteId', siteId)
    query.set('format', format)

    const response = await fetch(`${this.baseUrl}/leads/export?${query}`, {
      credentials: 'include',
      headers: {
        'X-Session-Token': useAuthStore.getState().token || '',
      },
    })

    return response.blob()
  }

  // Team
  async getTeamMembers() {
    return this.request<{ members: TeamMember[] }>('/team/members')
  }

  async addTeamMember(data: { email: string; name?: string; role?: string }) {
    return this.request<{ member: TeamMember }>('/team/members', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateTeamMember(
    id: string,
    data: { name?: string; role?: string; status?: string }
  ) {
    return this.request<void>(`/team/members/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteTeamMember(id: string) {
    return this.request<void>(`/team/members/${id}`, { method: 'DELETE' })
  }

  // Batch (YOLO mode)
  async queueBatch(domains: string[], strategy?: string, autoProcess?: boolean) {
    return this.request<{
      batchId: string
      queued: number
      skipped: number
      items: Array<{ id: string; domain: string }>
    }>('/batch', {
      method: 'POST',
      body: JSON.stringify({ domains, strategy, autoProcess }),
    })
  }

  async getBatchStatus(batchId?: string) {
    const query = batchId ? `?batchId=${batchId}` : ''
    const data = await this.request<any>(`/batch/status${query}`)
    const queue = (data.queue || data.items || []).map((item: any) => ({
      id: item.id,
      domainName: item.domainName || item.domain_name,
      status: item.status,
      resultJson: item.resultJson || item.result || null,
      errorMessage: item.errorMessage || item.error_message,
      createdAt: item.createdAt || item.created_at,
    }))

    return { ...data, queue }
  }

  async getBatch(batchId: string) {
    const data = await this.request<any>(`/batch/${batchId}`)
    const queue = (data.queue || data.items || []).map((item: any) => ({
      id: item.id,
      domainName: item.domainName || item.domain_name,
      status: item.status,
      resultJson: item.resultJson || item.result || null,
      errorMessage: item.errorMessage || item.error_message,
      createdAt: item.createdAt || item.created_at,
    }))

    return { ...data, queue }
  }

  async retryBatch(batchId: string) {
    return this.request<{ retriedCount: number }>(`/batch/${batchId}/retry`, {
      method: 'POST',
    })
  }

  async retryBatchItem(itemId: string) {
    return this.request<void>(`/batch/item/${itemId}/retry`, {
      method: 'POST',
    })
  }

  async cancelBatch(batchId: string) {
    return this.request<void>(`/batch/${batchId}`, { method: 'DELETE' })
  }

  async importDomains(file: File) {
    const formData = new FormData()
    formData.append('file', file)

    const response = await fetch(`${this.baseUrl}/batch/import`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'X-Session-Token': useAuthStore.getState().token || '',
      },
      body: formData,
    })

    const data = await response.json()
    if (!data.success) {
      throw new Error(data.error || 'Import failed')
    }

    return data.data as {
      parsed: number
      total: number
      domains: string[]
      truncated: boolean
    }
  }

  // Stripe
  async createCheckout(tier: string) {
    return this.request<{ url: string; sessionId: string }>('/stripe/checkout', {
      method: 'POST',
      body: JSON.stringify({ tier }),
    })
  }

  async createPortalSession() {
    return this.request<{ url: string }>('/stripe/portal', {
      method: 'POST',
    })
  }

  // Alias for createPortalSession
  async createPortal() {
    return this.createPortalSession()
  }

  async getSubscription() {
    return this.request<{
      tier: string
      status: string
      domainLimit: number
      domainsUsed: number
      currentPeriodEnd: string | null
      cancelAtPeriodEnd: boolean
      invoices: Array<{
        id: string
        amount: number
        currency: string
        status: string
        date: string
        pdfUrl: string
      }>
    }>('/stripe/subscription')
  }

  async cancelSubscription() {
    return this.request<void>('/stripe/cancel', { method: 'POST' })
  }

  async reactivateSubscription() {
    return this.request<void>('/stripe/reactivate', { method: 'POST' })
  }

  async getPrices() {
    return this.request<{
      tiers: Array<{
        id: string
        name: string
        price: number
        domains: number
        features: string[]
        popular?: boolean
      }>
    }>('/stripe/prices')
  }

  // Templates
  async getTemplates(strategy?: string, category?: string) {
    const params = new URLSearchParams()
    if (strategy) params.set('strategy', strategy)
    if (category) params.set('category', category)
    const query = params.toString()
    return this.request<{
      templates: DesignTemplatePreview[]
      total: number
    }>(`/templates${query ? `?${query}` : ''}`)
  }

  async getTemplateCategories() {
    return this.request<Array<{ category: string; label: string; count: number }>>(
      '/templates/categories'
    )
  }

  async getTemplate(id: string) {
    return this.request<DesignTemplate>(`/templates/${id}`)
  }

  async getDefaultTemplate(strategy: string) {
    return this.request<DesignTemplate>(`/templates/default/${strategy}`)
  }

  async getColorPalettes() {
    return this.request<Array<{
      id: string
      name: string
      colors: DesignConfig['colorScheme']
      isDark: boolean
    }>>('/templates/palettes')
  }

  async getTypographyOptions() {
    return this.request<Array<{
      id: string
      name: string
      headingFont: string
      bodyFont: string
    }>>('/templates/typography')
  }

  async previewDesignConfig(options: {
    templateId?: string
    palette?: string
    typography?: string
    aesthetic?: string
    layout?: string
    customColors?: Partial<DesignConfig['colorScheme']>
  }) {
    return this.request<DesignConfig>('/templates/preview', {
      method: 'POST',
      body: JSON.stringify(options),
    })
  }
}

function mapLead(raw: any): Lead {
  return {
    id: raw.id,
    siteId: raw.siteId || raw.site_id,
    email: raw.email,
    name: raw.name ?? null,
    domainName: raw.domainName || raw.domain_name,
    siteType: raw.siteType || raw.site_type,
    status: raw.status,
    source: raw.source ?? null,
    notes: raw.notes ?? null,
    assignedTo:
      raw.assignedTo ||
      (raw.assigned_id
        ? {
            id: raw.assigned_id,
            name: raw.assigned_name ?? null,
            email: raw.assigned_email ?? null,
            role: raw.assigned_role ?? null,
            status: raw.assigned_status ?? 'active',
          }
        : null),
    customFields: raw.customFields ?? null,
    createdAt: raw.createdAt || raw.created_at,
  }
}

export const api = new ApiClient(API_BASE)

// Types
export interface User {
  id: string
  email: string
  name: string | null
  subscriptionTier: string
  cloudflareConnected: boolean
  cloudflareAccountId: string | null
  domainLimit: number
  domainsUsed: number
}

export interface Domain {
  id: string
  domain_name: string
  cloudflare_zone_id: string | null
  verification_status: 'pending' | 'verifying' | 'verified' | 'failed'
  monetization_strategy: string | null
  aiAnalysis: DomainAnalysis | null
  monthly_revenue: number
  total_visits: number
  total_leads: number
  created_at: number
}

export interface DomainAnalysis {
  domain: string
  keywords: string[]
  niche: string
  targetAudience: string
  commercialIntent: number
  suggestedStrategies: Array<{
    strategy: string
    fitScore: number
    reasoning: string
  }>
  competitorExamples: string[]
  seoKeywords: string[]
  contentIdeas: string[]
}

export interface Site {
  id: string
  domain_id: string
  domain_name?: string
  status: 'draft' | 'published' | 'archived'
  site_type: string
  content: SiteContent | null
  design: DesignConfig | null
  deployed_url: string | null
  total_visits: number
  total_leads: number
  created_at: number
}

export interface SiteContent {
  title: string
  tagline: string
  hero: {
    headline: string
    subheadline: string
    ctaText: string
    ctaUrl: string
    image?: string
  }
  sections: Array<{
    type: string
    title: string
    content: unknown
  }>
  footer: {
    copyright: string
    links: Array<{ label: string; url: string }>
  }
  seo: {
    metaTitle: string
    metaDescription: string
    ogImage: string | null
  }
}

export interface DesignConfig {
  aesthetic: string
  colorScheme: {
    primary: string
    secondary: string
    accent: string
    background: string
    foreground: string
  }
  typography: {
    headingFont: string
    bodyFont: string
  }
  layout: string
}

export interface Lead {
  id: string
  siteId: string
  email: string
  name: string | null
  domainName?: string
  siteType?: string
  status: string
  source: string | null
  notes?: string | null
  assignedTo?: TeamMember | null
  customFields: Record<string, unknown> | null
  createdAt: number
}

export interface ClarifyingQuestion {
  question: string
  options: string[]
  allowCustom: boolean
}

export interface TeamMember {
  id: string
  email: string
  name: string | null
  role: string
  status: string
  invited_at?: number | null
  joined_at?: number | null
  created_at?: number
  updated_at?: number
}

export type TemplateCategory = 'classic' | 'modern' | 'bold' | 'elegant' | 'creative' | 'minimal' | 'dark'

export interface DesignTemplatePreview {
  id: string
  name: string
  category: TemplateCategory
  description: string
  preview?: string
  suitableFor: string[]
  aesthetic: string
  layout: string
  isDark: boolean
}

export interface DesignTemplate {
  id: string
  name: string
  category: TemplateCategory
  description: string
  preview?: string
  config: DesignConfig
  suitableFor: string[]
}
